// Coupled thermal loci: a locus's Energy this tick is the sum of every real
// influence touching it -- room ambient, contact with a marked floor tile, a
// live Bind·Energy link -- computed from one shared snapshot and applied once,
// rather than three sequential rules each seeing whatever the last one already
// changed.
//
// Added 2026-08-06 alongside `ThermalContactDef` (types.ts). Before this,
// `applyRoomDecay` (systems.ts) pulled a single locus toward a single
// target (ambient, or ambient+bias for a heatState object, or its own
// maxProperties ceiling) and `settleEnergyBind` separately equilibrated a
// Bind pair -- fine as long as no locus could ever be touched by two of
// these in the same tick. Contact conduction breaks that: the player
// standing on a hot floor while Bound to something cold has three
// influences live at once, and applying them one after another is order-
// dependent (whichever runs last dominates) -- not merely inelegant, but
// physically wrong. This module is the fix, scoped to exactly the loci that
// can now have more than one edge: the player always (ambient, plus
// contact and/or Bind when active), and any object that is either side of
// a live Bind·Energy link (which, once Bind moved onto this same tick
// cadence -- see the diary entry for why -- can simultaneously want its own
// ambient/regen edge too). Every other object (the overwhelming majority --
// room4's Ice Block, every Fireplace/Candle, the Bonfire) has at most one
// influence and keeps using `applyRoomDecay`'s existing loop untouched;
// folding those in too, so literally nothing decays outside this module, is
// recorded as a deferred follow-up in `09_Roadmap.md`, not done here.
//
// **Energy only, and the rate is the reason (2026-08-08).** `Bind`·`Charge`
// lived here for a day, as a second entry in a `SOLVED_CONCEPTS` list, on the
// theory that "two loci equilibrate" is one shape over two properties. It is
// not, and the difference is a time constant. Thermal diffusion through a solid
// genuinely is slow, so Energy closes a *fraction of the gap* per tick and a
// Bind·Energy link is a standing condition you can walk through -- which is
// what room13's gauntlet is built on. Charge redistribution through a conductor
// is instantaneous at any scale a player perceives, so a wire equalizes totally,
// on settle, and now lives in `chargeBind.ts` with its own cadence. The
// per-concept generality added for it went back out with it rather than staying
// as dead shape; see that module's header for the whole argument.
//
// Deliberately NOT extended to `VinesDef` -- vines have their own ambient-
// toward rule (`decayVines`) with no equivalent eligibility check here, so a
// bind-linked vine (nothing shipped does this) would only ever get its bind
// edge from this module while `decayVines` keeps running unmodified via
// `applyRoomDecay`, the same sequential-ordering gap this module exists to
// close elsewhere. Fine while nothing exercises it; flagged rather than
// silently mishandled.
import { WorldState } from './state';
import { countedDelta, proportionalDelta } from './physics';
import { RoomDef, ObjectDef } from './types';
import { DIRECTIONS, DIRECTION_OFFSETS } from './constants';
import { SimEvents } from './events';
import { ConceptId } from '../runes/concepts';
import { checkThermalDanger } from './player';
import { checkMeltThreshold } from './mechanics';
import { heatTarget } from './heatState';
import { holdsFluid } from './queries';

/** One influence on a locus's value this tick, in one of the two shapes
 * `physics.ts` defines: **exactly one** of `conductance` (a fraction of the
 * remaining gap) or `perTick` (a fixed number of units, stopping on arrival).
 *
 * Which one an edge carries is a claim about what the edge is for, and the two
 * families are worth being able to name (2026-08-12):
 *
 *   counted        ambient decay, a `heatState` ladder's own equilibrium, a
 *                  `maxProperties` refill -- all `room.decayPerTick`. The point
 *                  of these is that the player can count them, so they arrive.
 *   proportional   `thermalContact` (standing on something hot), fluid-to-fluid
 *                  conduction, and `Bind`·`Energy`. The point of these is that
 *                  the size of the gap *is* the severity: Lava at 30 and a
 *                  Scorched Floor at 10 are the same cluster at the same
 *                  conductance and differ only in how hot they are, so a fixed
 *                  step per tick would make them equally dangerous.
 *
 * `reciprocal` is set only when the other side is a
 * real, mutable locus that should receive the exact negated delta --
 * omitted for room ambient (a scalar, nothing to write back to) and for a
 * `thermalContact` edge whose `reciprocal` flag is false (a one-way read,
 * the "lava should feel inexhaustible" case -- see ThermalContactDef). A
 * reciprocal edge's own delta is clamped against both sides' pre-tick
 * values before it's ever summed with anything else -- the real
 * conservation guarantee `EnergyBindDef`/`ChargeBindDef` promise ("whichever
 * side would clamp limits how much actually moves, rather than losing or
 * inventing the difference") holds edge-by-edge, not just approximately for
 * the tick as a whole. A non-reciprocal edge (ambient; a one-way contact
 * source) has nothing on the other end to protect, so it's never clamped
 * until the final per-locus floor/ceiling applied in `solveThermalStep` --
 * the same "discard on clamp" rule `drawThermal`/`giveThermal` already have
 * for an ordinary draw against an infinite-in-practice source. */
export interface ThermalEdge {
  targetValue: number;
  /** Fraction of the remaining gap closed per tick. Mutually exclusive with
   * `perTick`. */
  conductance?: number;
  /** Units moved toward the target per tick, never past it. Mutually exclusive
   * with `conductance`. */
  perTick?: number;
  reciprocal?: ThermalLocus;
}

/** Either endpoint a `ThermalEdge` can touch. The player is not an
 * `ObjectDef`, so this can't just be "an object id" the way every other locus
 * reference in this codebase is. The `concept` on the object variant is what
 * a `thermalContact` edge reads (`ThermalContactDef.concept` is generic even
 * though every use today is Energy); the player's side is always their
 * `thermalState`, which *is* their Energy locus under the name the HUD and the
 * two danger thresholds use. */
export type ThermalLocus = { kind: 'player' } | { kind: 'object'; obj: ObjectDef; concept: ConceptId };

function locusKey(locus: ThermalLocus): string {
  return locus.kind === 'player' ? 'player' : locus.obj.id;
}

function currentValueOf(world: WorldState, locus: ThermalLocus): number {
  return locus.kind === 'player' ? world.player.thermalState : (locus.obj.properties?.[locus.concept] ?? 0);
}

/** Nothing has a ceiling any more (2026-08-09). The player used to be capped at
 * `maxThermalState`, which had two bad consequences and one good one nobody was
 * relying on. The bad ones: heat arriving at an already-hot caster was silently
 * discarded, so being at the top of the scale made them *immune* to further
 * heating -- Bind-ing yourself to a 20-Energy vessel while capped moved nothing
 * at all -- and there was no headroom for anything to be more dangerous than
 * anything else, since the reading could never exceed the cap by more than the
 * cap allowed. A temperature has no maximum; the *bar* does, and `updateStatsHud`
 * is where that belongs.
 *
 * The floor at 0 stays, applied where the deltas land: that one is real, and it
 * is what keeps an exchange from inventing a quantity to give away. */
function ceilingOf(_world: WorldState, _locus: ThermalLocus): number {
  return Infinity;
}

/** The player's own edges this tick: always ambient, plus contact with
 * whatever `thermalContact` object (if any) currently occupies their own tile.
 *
 * Bind edges reaching the player are deliberately not collected here -- see
 * `collectObjectEdges`'s own comment on why a Bind link is represented exactly
 * once, from the object side, with its `reciprocal` doing the work of reaching
 * back to the player. */
function collectPlayerEdges(world: WorldState, room: RoomDef): ThermalEdge[] {
  const physics = world.physics;
  const ambient = room.ambientEnergy ?? physics.player.thermalStart;
  const edges: ThermalEdge[] = [{ targetValue: ambient, conductance: physics.player.thermalDecayRate }];

  const contact = room.objects.find((o) => o.thermalContact && o.x === world.player.x && o.y === world.player.y);
  if (contact) {
    const tc = contact.thermalContact!;
    edges.push({
      targetValue: contact.properties?.[tc.concept] ?? 0,
      conductance: tc.conductance,
      reciprocal: tc.reciprocal ? { kind: 'object', obj: contact, concept: tc.concept } : undefined,
    });
  }
  return edges;
}

/** `obj`'s own edges this tick for one concept -- called once per locus in
 * `involvedObjects` (see `solveThermalStep`), which includes both sides of
 * every live Bind link: the object that declares the link (whose own edge
 * reaches its partner, player or object, via `reciprocal`) and, for an
 * object-to-object link, the partner it points at too (so *that* side's own
 * ambient/regen edge is collected here instead of leaking through
 * `applyRoomDecay`'s separate, unmodified loop and double-applying against
 * this tick's Bind adjustment -- the same ordering bug this module exists to
 * close). A partner with no link of its own gets no bind edge here; its half
 * of the link is already fully represented by the declaring object's own edge
 * and that edge's `reciprocal` write-back. */
function collectObjectEdges(world: WorldState, room: RoomDef, obj: ObjectDef): ThermalEdge[] {
  const physics = world.physics;
  const ambient = room.ambientEnergy ?? physics.player.thermalStart;
  const edges: ThermalEdge[] = [];

  // Ambient/regen eligibility mirrors applyRoomDecay's own three branches
  // exactly -- same fields, same mutual exclusion (a meltsWhen sink and a
  // maxProperties source are still never the same object).
  if (obj.properties?.energy !== undefined && obj.barrier?.meltsWhen && !obj.heatState) {
    edges.push({ targetValue: ambient, perTick: physics.room.decayPerTick });
  }
  if (obj.heatState) {
    edges.push({ targetValue: heatTarget(obj, ambient), perTick: physics.room.decayPerTick });
  }
  // `applyRoomDecay` skips these objects outright (see `bindInvolvedObjects`),
  // so its Energy regen has to be reproduced here or it silently stops
  // happening. Only Energy: an Energy-bound object with a `maxProperties`
  // entry for some *other* concept would still lose that regen, which
  // `sim.test.ts` pins as authored-world-empty rather than leaving to be
  // discovered as a reservoir that quietly went flat. Charge specifically is
  // no longer at risk here -- a Charge-bound object is not skipped by
  // `applyRoomDecay` at all any more (see chargeBind.ts).
  // Skipped for a heatState object governing Energy, exactly as in
  // `applyRoomDecay` -- the ladder's own edge above already is this object's
  // equilibrium, and a second edge pulling at the cap would relight whatever
  // the ladder just put out.
  if (obj.properties && obj.maxProperties?.energy !== undefined && obj.heatState?.concept !== 'energy') {
    edges.push({ targetValue: obj.maxProperties.energy, perTick: physics.room.decayPerTick });
  }

  // A fluid carries heat along itself, and into whatever other fluid it
  // touches: one reciprocal edge per orthogonally adjacent flowing tile
  // (2026-08-09). This is the fourth edge kind, and the one that makes lava
  // meeting water mean something without either of them being told about the
  // other -- lava is simply hot, water is simply not, and the gap closes.
  //
  // **Only while both tiles are flowing.** The flow is what carries the heat,
  // so a frozen tile is out of the circuit at both ends. That is a physical
  // statement, and it is also what leaves room9's freeze puzzle untouched: a
  // tile frozen to redirect the river has no warm neighbour pulling it back
  // over its own melting point, because freezing dropped it out of the flow in
  // the same instant. A consequence of the rule, not an exception carved for
  // the puzzle.
  //
  // Represented from exactly one side of each pair, picked by id, for the same
  // reason the Bind branch below does it: two edges over one gap would close it
  // at double the configured rate.
  if (obj.properties?.energy !== undefined && holdsFluid(obj, world.flowingTiles)) {
    const ownFluid = obj.water!.fluid ?? 'water';
    for (const dir of DIRECTIONS) {
      const offset = DIRECTION_OFFSETS[dir];
      const neighbour = room.objects.find(
        (o) => o.x === obj.x + offset.x && o.y === obj.y + offset.y && o.water !== undefined && o.properties?.energy !== undefined,
      );
      if (!neighbour || !holdsFluid(neighbour, world.flowingTiles)) continue;
      if (neighbour.id < obj.id) continue;
      // Two materials in series: the interface is governed by both, so the
      // pair's conductances average. Same-fluid neighbours simply average to
      // their own.
      const conductance = (physics.fluid[ownFluid].conductance + physics.fluid[neighbour.water!.fluid ?? 'water'].conductance) / 2;
      edges.push({ targetValue: neighbour.properties!.energy!, conductance, reciprocal: { kind: 'object', obj: neighbour, concept: 'energy' } });
    }
  }

  if (obj.energyBind) {
    const conductance = physics.bind.energyEquilibrationRate;
    const sourceId = obj.energyBind.sourceId;
    if (sourceId === undefined) {
      edges.push({ targetValue: world.player.thermalState, conductance, reciprocal: { kind: 'player' } });
    } else {
      const partner = room.objects.find((o) => o.id === sourceId);
      // One link, one edge -- even when both ends declare it. Nothing stops a
      // player from binding A to B and then B to A (each cast is a legal,
      // ordinary re-bind), and before this guard that pair contributed *two*
      // edges over the same gap, so it equilibrated at double the configured
      // rate. Harmless at Energy's 0.2 (doubled is 0.4, still under 1.0, so it
      // merely converged twice as fast) and never noticed until Bind·Charge's
      // faster rate turned the same quirk into a perpetual oscillation -- see
      // `12_Mechanics_Charge.md`. Charge has since moved to total equalization
      // in chargeBind.ts, where the arrangement is not expressible at all;
      // this guard is what keeps the rule true for the concept that still has
      // a rate. Resolved by representing the pair from exactly one side,
      // picked by id so the choice is deterministic rather than dependent on
      // room-object order. A link to the caster needs no such guard: the
      // player is not an ObjectDef and cannot declare one back.
      const partnerLinksBack = partner?.energyBind?.sourceId === obj.id;
      if (partner?.properties?.energy !== undefined && !(partnerLinksBack && partner.id < obj.id)) {
        edges.push({ targetValue: partner.properties.energy, conductance, reciprocal: { kind: 'object', obj: partner, concept: 'energy' } });
      }
    }
  }
  return edges;
}

/** Both sides of every live `Bind`·`Energy` link in the room: the object that
 * declares the link, and, for an object-to-object link, the partner it names
 * too. Exported so `applyRoomDecay` (systems.ts) can skip exactly these
 * objects -- `solveThermalStep` already gives each of them its own
 * ambient/regen edges, so leaving them in `applyRoomDecay`'s own separate loop
 * would double-apply against this tick's Bind adjustment, the same ordering
 * bug this whole module exists to close.
 *
 * `Bind`·`Charge` links are deliberately *not* included, as of 2026-08-08.
 * They no longer conduct on this cadence at all (they equalize totally, on
 * settle -- see chargeBind.ts), so a Charge-bound object has nothing happening
 * to it here and belongs in `applyRoomDecay`'s ordinary loop like any other
 * object; skipping it there would strip its Energy decay and its
 * `maxProperties` regen for no reason. An object carrying both links is
 * covered by its Energy one, which is the only one this module still solves. */
export function bindInvolvedObjects(room: RoomDef, flowing?: ReadonlySet<string>): ObjectDef[] {
  const boundObjects = room.objects.filter((o) => o.energyBind);
  const partnerIds = new Set(boundObjects.map((o) => o.energyBind!.sourceId).filter((id): id is string => id !== undefined));
  // Flowing fluid tiles are solved here too (2026-08-09), because they now
  // carry edges of their own -- a fluid conducts along itself and into
  // whatever other fluid it touches, so a lava channel running beside a water
  // one has to be *visited* by the solver, not merely reachable as somebody
  // else's reciprocal target. `applyRoomDecay` reads the same list to know
  // what to leave alone, so widening it here keeps the two in step by
  // construction rather than by memory. A tile out of the flow is not in the
  // circuit and is not listed.
  const isSolvedFluid = (o: ObjectDef) => o.properties?.energy !== undefined && !!flowing && holdsFluid(o, flowing);
  return room.objects.filter((o) => boundObjects.includes(o) || partnerIds.has(o.id) || isSolvedFluid(o));
}

/** Runs one tick's worth of coupled-locus Energy influence: the player, plus
 * both sides of every live `Bind`·`Energy` link in the current room.
 * Snapshot-then-apply, not sequential mutation -- every edge's delta is
 * computed from the value each locus held *before* this call, summed per locus
 * (a Bind pair's two contributions cancel exactly, conserving the pair's total
 * the same way `settleEnergyBind` used to), and only then written back once.
 * Called from `tick()` on the room-decay cadence, replacing the old
 * `applyPlayerThermalDecay` call and the separate `energy-bind` settle system
 * -- see the module comment for why both had to move here together. */
export function solveThermalStep(world: WorldState, room: RoomDef, out: SimEvents): void {
  const involvedObjects = bindInvolvedObjects(room, world.flowingTiles);

  // Every object touched by anything below -- an edge owner (involvedObjects)
  // or purely a reciprocal target (a `thermalContact` floor with
  // `reciprocal: true`, never itself an edge owner) -- has to be resolvable
  // in the apply phase below, not just whichever objects happened to have
  // their own edges collected.
  const touchedObjects = new Map<string, ObjectDef>(involvedObjects.map((o) => [o.id, o]));
  const deltas = new Map<string, number>();
  const add = (locus: ThermalLocus, amount: number) => {
    if (locus.kind === 'object') touchedObjects.set(locus.obj.id, locus.obj);
    deltas.set(locusKey(locus), (deltas.get(locusKey(locus)) ?? 0) + amount);
  };

  const applyLocusEdges = (locus: ThermalLocus, edges: ThermalEdge[]) => {
    const current = currentValueOf(world, locus);
    const ceiling = ceilingOf(world, locus);
    for (const edge of edges) {
      // **A reciprocal edge may only travel half the gap**, because both ends
      // move: this locus gains `delta` and the partner loses it, so they meet at
      // the midpoint and anything beyond that is the two of them swapping places.
      //
      // Free under the old all-proportional world -- every conductance in the
      // game is <= 0.5, so the midpoint was never reachable in one tick and
      // nothing needed to say so. Not free now. A counted edge's step is an
      // absolute number with no relationship to the gap at all, so on a gap of
      // 0.4 a step of 1.5 would sail past the meeting point and land the pair
      // *further* apart than they started, oscillating for as long as the link
      // lives. Stated as an invariant rather than left to each shape to respect,
      // because the next mechanism to get an edge will not know to.
      // A *cap*, not a scaling: the step is computed from the whole gap and then
      // limited, so every proportional edge in the game moves exactly as far as
      // it did before this guard existed (no conductance is above 0.5, so none of
      // them ever reaches the cap) and only a counted edge on a nearly-closed gap
      // is actually held back.
      const gap = edge.targetValue - current;
      const limit = edge.reciprocal ? Math.abs(gap) / 2 : Math.abs(gap);
      const raw = edge.perTick !== undefined ? countedDelta(gap, edge.perTick) : proportionalDelta(gap, edge.conductance!);
      let delta = Math.sign(raw) * Math.min(Math.abs(raw), limit);
      if (edge.reciprocal) {
        // Real, two-sided conservation, edge-by-edge -- symmetric to
        // settleEnergyBind's own exact clamp (Math.min(Math.max(desired,
        // floor), ceiling)), generalized to whichever kind of locus each
        // side is. edge.targetValue already *is* the partner's own pre-tick
        // value (that's how every reciprocal edge is built in
        // collectPlayerEdges/collectObjectEdges), so it doubles as
        // `partnerValue` here without a second lookup.
        const partnerValue = edge.targetValue;
        const partnerCeiling = ceilingOf(world, edge.reciprocal);
        const lower = Math.max(-current, partnerValue - partnerCeiling);
        const upper = Math.min(ceiling - current, partnerValue);
        delta = Math.min(Math.max(delta, lower), upper);
      }
      add(locus, delta);
      if (edge.reciprocal) add(edge.reciprocal, -delta);
    }
  };

  applyLocusEdges({ kind: 'player' }, collectPlayerEdges(world, room));
  for (const obj of involvedObjects) {
    applyLocusEdges({ kind: 'object', obj, concept: 'energy' }, collectObjectEdges(world, room, obj));
  }

  // Apply phase -- every touched locus exactly once. Each reciprocal edge
  // was already clamped against its own two endpoints above, so this final
  // clamp is a backstop for the rarer case a locus's *summed* total still
  // overshoots (an ambient/non-reciprocal-contact edge pushing the same
  // direction a correctly-clamped Bind edge already used the last of the
  // headroom for) -- not the common path, unlike before this module existed.
  //
  // The player is always touched (the ambient edge is unconditional), so
  // checkThermalDanger always runs, matching the old applyPlayerThermalDecay's
  // own unconditional call (sustained exposure at a steady-state value still
  // has to keep reading as dangerous, not just the tick a threshold is first
  // crossed).
  const playerDelta = deltas.get('player') ?? 0;
  world.player.thermalState = Math.max(0, world.player.thermalState + playerDelta);
  checkThermalDanger(world, out);

  for (const obj of touchedObjects.values()) {
    if (!obj.properties) continue;
    const delta = deltas.get(obj.id) ?? 0;
    if (delta !== 0) {
      // Floor only, no ceiling -- objects have none today, the same rule
      // settleEnergyBind's own clamp always applied.
      obj.properties.energy = Math.max(0, (obj.properties.energy ?? 0) + delta);
    }
    checkMeltThreshold(room, obj, out, world.physics, world.flowingTiles);
  }
}
